<template>
	<div
		:title="value"
		:class="[fontFamily, color, {solid: isSolid}]"
		class="tag"
		@click="onClick"
	>
		<div v-if="icon" :class="[color]" class="tag-icon">
			<t-timpex-icon :type="type" :icon="icon" size="sm"/>
		</div>

		<div class="tag-inner">
			<slot>{{value}}</slot>
		</div>

		<div
			v-if="!notRemovable"
			class="tag-button"
			tabindex="0"
			@keydown.space="onSpace"
			@click="onRemove"
		>
			<t-timpex-icon type="bp" size="sm" icon="cross" />
		</div>
	</div>
</template>

<script>
import TTimpexIcon from '../timpex/icons/TTimpexIcon';
import badgeMixin from './badgeMixin';
export default {
	name: 'TTag',
	components: { TTimpexIcon },
	mixins: [badgeMixin],
	props: {
		icon: String,
		type: String,
		notRemovable: Boolean,
		color: {
			type: String,
			default: 'blue'
		},
	},
	methods: {
		onRemove(event) {
			event.stopPropagation();
			this.$emit('remove', event);
		},
		onClick(event) {
			this.$emit('click', event);
		},
		onSpace(event) {
			window.focusNext(event);
			this.onRemove(event);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/text';
@import '~@/components/evergreen/scss/sizes/functions';
@import './badgeMixin';

.tag {
	@extend %text-small;
	border-radius: #{getBorderRadiusForControlHeight(32)}px;
	background-color: get-color($blue-scale, 'B3A');
	color: get-color($text, 'default');
	box-shadow: inset 0 0 0 1px get-color($blue-scale, 'B4A'),
				inset 0 1px 1px 0 get-color($blue-scale, 'B3A');
	font-weight: 600;
	display: flex;
	align-items: center;
	height: 32px;
	margin-top: 0;
	&.blue {
		background-color: get-color($blue-scale, 'B3A');
		box-shadow: inset 0 0 0 1px get-color($blue-scale, 'B4A'),
				inset 0 1px 1px 0 get-color($blue-scale, 'B3A');
	}
	&.green {
		color: get-color($green, 'dark');
		background-color: get-color($green, 'lightest');
		box-shadow: inset 0 0 0 1px get-color($green, 'light'),
				inset 0 1px 1px 0 get-color($green, 'lightest');
	}
	&.neutral {
		background-color: get-color($neutral-scale, 'N3A');
		box-shadow: inset 0 0 0 1px get-color($neutral-scale, 'N4A'),
				inset 0 1px 1px 0 get-color($neutral-scale, 'N3A');
	}
	&.red {
		color: get-color($red, 'dark');
		background-color: get-color($red, 'lightest');
		box-shadow: inset 0 0 0 1px get-color($red, 'light'),
				inset 0 1px 1px 0 get-color($red, 'lightest');
	}
	&.yellow {
		background-color: get-color($yellow, 'lightest');
		box-shadow: inset 0 0 0 1px get-color($yellow, 'light'),
				inset 0 1px 1px 0 get-color($yellow, 'lightest');
	}
	
	//FontFamily
	&.ui {
		@extend %font-families-ui;
	}
	&.display {
		@extend %font-families-display;
	}
	&.mono {
		@extend %font-families-mono;
	}
}

.tag-icon {
	color: get-color($blue, 'base');
	margin-left: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	&.blue {
		color: get-color($blue, 'base');
	}
	&.green {
		color: get-color($green, 'dark');
	}
	&.neutral {
		color: get-color($blue, 'base');
	}
	&.red {
		color: get-color($red, 'dark');
	}
	&.yellow {
		color: get-color($yellow, 'base');
	}
	
}

.tag-inner {
	flex:1;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	margin-left: 8px;
	user-select: none;
}

.tag-button {
	outline: none;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	width: 30px;
	height: 30px;
	&:hover {
		background-color: get-color($neutral-scale, 'N2A');
	}
	&:focus, &:active {
		background-color: get-color($neutral-scale, 'N3A');
	}
}

</style>


<docs>
## Examples

### Default

```jsx noeditor
<div style="display: grid; grid-column-gap: 8px; grid-row-gap: 8px; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); width: 100%;">
	<t-tag>Ålesund (6000)</t-tag>
	<t-tag>Hareidlandet</t-tag>
	<t-tag>Gurskøy</t-tag>
	<t-tag color="red">Utenfor Ålesundregionen</t-tag>
</div>
```
#### Example Code
```html
	<t-tag>Ålesund (6000)</t-tag>
	<t-tag>Hareidlandet</t-tag>
	<t-tag>Gurskøy</t-tag>
	<t-tag color="red">Utenfor Ålesundregionen</t-tag>
```
### Icon

```jsx noeditor
<div style="display: grid; grid-column-gap: 8px; grid-row-gap: 8px; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); width: 100%;">
	<t-tag type="bp" icon="map-marker">Ålesund (6000)</t-tag>
	<t-tag type="bp" icon="polygon-filter">Hareidlandet</t-tag>
	<t-tag type="bp" icon="polygon-filter">Gurskøy</t-tag>
	<t-tag color="red" type="bp" icon="polygon-filter">Utenfor Ålesundregionen</t-tag>
</div>
```
#### Example Code
```html
	<t-tag type="bp" icon="map-marker">Ålesund (6000)</t-tag>
	<t-tag type="bp" icon="polygon-filter">Hareidlandet</t-tag>
	<t-tag type="bp" icon="polygon-filter">Gurskøy</t-tag>
	<t-tag color="red" type="bp" icon="polygon-filter">Utenfor Ålesundregionen</t-tag>
```

</docs>
